/**
 * รอบจ่ายเงินเดือน + ตัวเลือกคิดเงินรายชั่วโมง — ฝ่ายบุคคลแก้ได้เอง (capability config_pay)
 *
 * TN Riches 21 ก.ย. 2569: ปุ่ม "แก้ไข" รอบจ่ายในพิมพ์เขียวพา HR ไป /owner/settings ที่เป็นของเจ้าของ
 * เท่านั้น → เด้งกลับแดชบอร์ดเงียบ ๆ พี่ปุ้ยเคาะ "ให้ HR แก้ได้" หน้านี้จึง render เฉพาะกลุ่ม
 * `hrScope: 'paycycle'` ของ FIELD_GROUPS (ชุดเดียวกับที่เจ้าของเห็นในตั้งค่า → รอบเงินเดือน)
 * ผ่าน /hr/pay-cycle-settings ซึ่ง allowlist คีย์ไว้ที่ HR_PAY_CYCLE_SETTING_KEYS ฝั่ง worker
 * (โหมดอนุมัติ/บัญชีธนาคารไม่อยู่ในชุดนี้โดยตั้งใจ)
 */
import { useEffect, useState } from 'react';
import { Save } from 'lucide-react';
import { Loading } from '../../components/layout/Loading';
import { LoadError } from '../../components/layout/LoadError';
import { useToast } from '../../components/ui/toast';
import { usePayCycleSettings, useUpdatePayCycleSettings } from '../../hooks';
import { FIELD_GROUPS, Field } from '../owner/Settings';

export function PayCycleSettingsContent() {
  const { data, isLoading, error, refetch } = usePayCycleSettings();
  const update = useUpdatePayCycleSettings();
  const toast = useToast();
  const [form, setForm] = useState<Record<string, string>>({});
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (data) setForm(data);
  }, [data]);

  if (isLoading) return <Loading />;
  if (error || !data) {
    return (
      <LoadError
        error={error}
        hint="ถ้าขึ้นว่าไม่มีสิทธิ์ ให้เจ้าของระบบเปิดสิทธิ์ 'ตั้งค่าเงินเพิ่ม/เงินหัก/คอมมิชชั่น/รอบจ่าย' ที่เมนู สิทธิ์ฝ่ายบุคคล"
        onRetry={() => refetch()}
      />
    );
  }

  function setField(key: string, value: string) {
    setForm((prev) => ({ ...prev, [key]: value }));
    setSaved(false);
  }
  async function handleSave() {
    try {
      await update.mutateAsync(form);
      setSaved(true);
      toast.success('บันทึกรอบจ่ายเงินเดือนแล้ว');
      setTimeout(() => setSaved(false), 2500);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'บันทึกไม่สำเร็จ');
    }
  }

  const groups = FIELD_GROUPS.filter((g) => g.hrScope === 'paycycle');
  return (
    <div className="space-y-4">
      <p className="text-sm text-gray-500">
        วันปิดงวดและวันจ่ายใช้กับการลา การแจ้งเตือน และการปิดงวดทั้งบริษัท — เปลี่ยนแล้วมีผลกับงวดถัดไปที่ยังไม่ปิด
      </p>
      {groups.map((g) => (
        <div key={g.title} className="card p-4">
          <div className="font-semibold mb-3">{g.title}</div>
          {g.warn?.(form) && (
            <div className="mb-3 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-800">{g.warn(form)}</div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {g.fields.map((f) => (
              <Field key={f.key} f={f} value={form[f.key] ?? ''} onChange={(v) => setField(f.key, v)} />
            ))}
          </div>
        </div>
      ))}
      <div className="flex items-center gap-3">
        <button onClick={handleSave} disabled={update.isPending} className="btn-primary">
          <Save className="w-4 h-4" />
          {update.isPending ? 'กำลังบันทึก…' : 'บันทึก'}
        </button>
        {saved && <span className="text-sm text-green-600">บันทึกแล้ว ✓</span>}
      </div>
    </div>
  );
}
